/**
 * Keep the phone screen on during a race.
 * Motion / tilt is not a user gesture, so the OS idle timeout would otherwise
 * lock the screen after ~2 minutes.
 */

let wakeLock = null;
let videoEl = null;
let wanted = false;
let visBound = false;

async function requestNativeWakeLock() {
  if (!wanted) return false;
  if (!navigator.wakeLock?.request) return false;
  try {
    wakeLock = await navigator.wakeLock.request("screen");
    wakeLock.addEventListener("release", () => {
      wakeLock = null;
      if (wanted && document.visibilityState === "visible") {
        requestNativeWakeLock();
      }
    });
    return true;
  } catch {
    wakeLock = null;
    return false;
  }
}

function ensureVideoFallback() {
  if (videoEl) {
    if (wanted) {
      const play = videoEl.play();
      if (play && typeof play.catch === "function") play.catch(() => {});
    }
    return;
  }
  if (typeof document === "undefined") return;

  const canvas = document.createElement("canvas");
  canvas.width = 2;
  canvas.height = 2;
  const ctx = canvas.getContext("2d");
  if (ctx) ctx.fillRect(0, 0, 2, 2);

  videoEl = document.createElement("video");
  videoEl.setAttribute("playsinline", "true");
  videoEl.setAttribute("webkit-playsinline", "true");
  videoEl.muted = true;
  videoEl.defaultMuted = true;
  videoEl.loop = true;
  videoEl.playsInline = true;
  videoEl.setAttribute("muted", "");
  videoEl.style.cssText =
    "position:fixed;left:0;bottom:0;width:1px;height:1px;opacity:0.01;pointer-events:none;";

  try {
    if (typeof canvas.captureStream === "function") {
      videoEl.srcObject = canvas.captureStream(1);
    }
  } catch {
    videoEl = null;
    return;
  }

  document.body.appendChild(videoEl);
  const play = videoEl.play();
  if (play && typeof play.catch === "function") play.catch(() => {});
}

function stopVideoFallback() {
  if (!videoEl) return;
  try {
    videoEl.pause();
  } catch {
    /* ignore */
  }
}

function onVisibility() {
  if (!wanted) return;
  if (document.visibilityState === "visible") {
    void acquire();
  }
}

async function acquire() {
  if (!wanted) return;
  const ok = await requestNativeWakeLock();
  if (!ok) ensureVideoFallback();
}

function bindVisibility() {
  if (visBound || typeof document === "undefined") return;
  document.addEventListener("visibilitychange", onVisibility);
  visBound = true;
}

export async function keepScreenAwake() {
  wanted = true;
  bindVisibility();
  await acquire();
}

export function allowScreenSleep() {
  wanted = false;
  if (wakeLock) {
    try {
      wakeLock.release();
    } catch {
      /* ignore */
    }
    wakeLock = null;
  }
  stopVideoFallback();
}
